import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Button, { BUTTON_POSITION, BUTTON_TYPE } from './Button';
import { ru } from 'date-fns/locale';
import { isSameDay } from 'date-fns';
import { DayPicker as ReactDayPicker, TZDate } from 'react-day-picker';
import Modal from './Modal';
import DatePeriod from './DatePeriod';
import DateFormat from './DateFormat';
import DateTZ from '../models/date/DateTZ';
import EraseIcon from "./icons/Erase";


export const DAY_PICKER_MODE = {
  RANGE: 'range',
  SINGLE: 'single',
  MULTIPLE: 'multiple'
};

export const APPLY_MODE = {
  BUTTON: 'button',
  AUTO: 'auto'
};

const TIMEZONE = 'UTC';

const toTZDate = (date = new Date()) => {
  if (date === null || date instanceof TZDate) {
    return date;
  } else {
    const dateTZ = new DateTZ(date);
    return dateTZ.dayjs ? new TZDate(dateTZ.dayjs.year(), dateTZ.dayjs.month(), dateTZ.dayjs.date(), TIMEZONE) : null;
  }
};

const DayPicker = ({ mode = null, className, selected, footer, formatters, onSelect, fromDate = null, onDayClick, toDate = null}) => {
  const [dates, setDates] = useState([]); //Array<Date>, { from: Date, to: Date }, Date
  const [startMonth, setStartMonth] = useState(null);
  const [endMonth, setEndMonth] = useState(null);
  const [selectedMode, setSelectedMode] = useState(null);

  const [defaultMonth] = useState((mode === DAY_PICKER_MODE.SINGLE && selected instanceof Date) ?
    toTZDate(selected) :
  ((mode === DAY_PICKER_MODE.MULTIPLE && selected instanceof Array && selected.length > 0) ?
    toTZDate(selected[0]) :
  ((mode === DAY_PICKER_MODE.RANGE && (selected.to || selected.from)) ?
    toTZDate(selected.to || selected.from) :
    null
  )));

  const handleDayClick = (day, modifiers) => {
    if (selectedMode === DAY_PICKER_MODE.MULTIPLE) {
      const newDate = [...dates];
      if (modifiers.selected) {
        const index = dates.findIndex((d) => isSameDay(day, d));
        newDate.splice(index, 1);
      } else {
        newDate.push(day);
      }
      setDates(newDate);
      typeof onDayClick === 'function' && onDayClick(newDate);
    } else {
      typeof onDayClick === 'function' && onDayClick(dates);
    }
  };

  const handleSelect = (dates) => {
    if (dates) {
      typeof onSelect === 'function' && onSelect(dates);
    }
    setDates(dates);
  };

  useEffect(() => {
    let selectedMode = mode;
    let selectedDates = toTZDate();
    if (selected instanceof Array) {
      selectedDates = selected.map((date) => toTZDate(date));
      if (!mode) selectedMode = DAY_PICKER_MODE.MULTIPLE;
    } else if (selected && (selected.from || selected.to)) {
      selectedDates = { from: toTZDate(selected.from), to: toTZDate(selected.to) };
      if (!mode) selectedMode = DAY_PICKER_MODE.RANGE;
    } else if (selected instanceof Date) {
      selectedDates = toTZDate(selected);
      if (!mode) selectedMode = DAY_PICKER_MODE.SINGLE;
    } else if (typeof selected === 'string' && selected.length) {
      selectedDates = toTZDate(selected);
      if (!mode) selectedMode = DAY_PICKER_MODE.SINGLE;
    } else {
      if (!mode) selectedMode = DAY_PICKER_MODE.SINGLE;
    }

    setSelectedMode(selectedMode);
    setDates(selectedDates);
  }, [selected, mode]);

  useEffect(() => {
    setStartMonth(toTZDate(fromDate));
    setEndMonth(toTZDate(toDate));
  }, [fromDate, toDate]);

  return (
    <>
      <ReactDayPicker
        timeZone={TIMEZONE}
        className={className}
        captionLayout="dropdown"
        startMonth={startMonth}
        endMonth={endMonth}
        defaultMonth={defaultMonth}
        mode={selectedMode}
        formatters={formatters}
        locale={ru}
        selected={dates}
        onSelect={handleSelect}
        onDayClick={handleDayClick}
        footer={footer}
      />
    </>
  );
}

DayPicker.propTypes = {
  mode: PropTypes.string,
  className: PropTypes.string,
  selected: PropTypes.any,
  footer: PropTypes.any,
  readOnly: PropTypes.bool,
  onSelect: PropTypes.func,
  onDayClick: PropTypes.func,
  fromDate: PropTypes.any,
  toDate: PropTypes.any
};

const DayPickerInput = ({ className = '', title, apply = APPLY_MODE.BUTTON, erase, readOnly = false, onChange, onCancel, children, ...dayPickerProps }) => {
  const {t} = useTranslation(['common']);

  const [dates, setDates] = useState([]); //Array<Date>, { from: Date, to: Date }, Date
  const [selectedMode, setSelectedMode] = useState(null);

  const [showSelectDate, setShowSelectDate] = useState(false);
  const handleSelectDateClose = () => setShowSelectDate(false);
  const handleSelectDateShow = () => setShowSelectDate(true);

  const handleApplyDate = useCallback(() => {
    handleSelectDateClose();
    if (!dates) {
      let defaultDates = null;
      if (selectedMode === DAY_PICKER_MODE.RANGE) {
        defaultDates = { from: toTZDate(), to: toTZDate() };
      } else if (selectedMode === DAY_PICKER_MODE.SINGLE) {
        defaultDates = toTZDate();
      } else if (selectedMode === DAY_PICKER_MODE.MULTIPLE) {
        defaultDates = [toTZDate()];
      }
      setDates(defaultDates);
      typeof onChange === 'function' && onChange(defaultDates);
    } else {
      let datesTZ = null;
      if (selectedMode === DAY_PICKER_MODE.RANGE) {
        datesTZ = { from: dates.from ? new DateTZ(dates.from) : null, to: dates.to ? new DateTZ(dates.to) : null };
      } else if (selectedMode === DAY_PICKER_MODE.SINGLE) {
        datesTZ = new DateTZ(dates);
      } else if (selectedMode === DAY_PICKER_MODE.MULTIPLE) {
        datesTZ = dates.map((date) => new DateTZ(date));
        datesTZ.sort((a, b) => a && b ? a.getTime() - b.getTime() : 0);
      }
      typeof onChange === 'function' && onChange(datesTZ);
    }
  }, [dates]);

  const handleSelect = (dates) => {
    setDates(dates);

    if (apply === APPLY_MODE.AUTO) {
      setTimeout(() => {
        typeof onChange === 'function' && onChange(dates);
      }, 300);
    }
  };

  const handleCancel = useCallback(() => {
    handleSelectDateClose();
    typeof onCancel === 'function' && onCancel();
  }, []);

  const handleErase = useCallback((e) => {
    e.stopPropagation();

    let dates = null;
    if (selectedMode === DAY_PICKER_MODE.RANGE) {
      dates = { from: null, to: null };
    } else if (selectedMode === DAY_PICKER_MODE.SINGLE) {
      dates = null;
    } else if (selectedMode === DAY_PICKER_MODE.MULTIPLE) {
      dates = [];
    }

    setDates(dates);
    handleSelectDateClose();
    typeof onChange === 'function' && onChange(dates);
  }, [dates]);

  useEffect(() => {
    let selectedMode = dayPickerProps.mode;
    let selectedDates = toTZDate();
    if (dayPickerProps.selected instanceof Array) {
      selectedDates = dayPickerProps.selected.map((date) => toTZDate(date));
      if (!dayPickerProps.mode) selectedMode = DAY_PICKER_MODE.MULTIPLE;
    } else if (dayPickerProps.selected && (dayPickerProps.selected.from || dayPickerProps.selected.to)) {
      selectedDates = { from: toTZDate(dayPickerProps.selected.from), to: toTZDate(dayPickerProps.selected.to) };
      if (!dayPickerProps.mode) selectedMode = DAY_PICKER_MODE.RANGE;
    } else if (dayPickerProps.selected instanceof Date) {
      selectedDates = toTZDate(dayPickerProps.selected);
      if (!dayPickerProps.mode) selectedMode = DAY_PICKER_MODE.SINGLE;
    } else if (typeof dayPickerProps.selected === 'string' && dayPickerProps.selected.length) {
      selectedDates = toTZDate(dayPickerProps.selected);
      if (!dayPickerProps.mode) selectedMode = DAY_PICKER_MODE.SINGLE;
    } else if (dayPickerProps.selected === null) {
      selectedDates = null;
      if (!dayPickerProps.mode) selectedMode = DAY_PICKER_MODE.SINGLE;
    } else {
      if (!dayPickerProps.mode) selectedMode = DAY_PICKER_MODE.SINGLE;
    }

    setSelectedMode(selectedMode);
    setDates(selectedDates);
  }, [dayPickerProps.selected, dayPickerProps.mode]);

  return (
    <>
      {children ? (
        <span className={className} onClick={() => !readOnly && handleSelectDateShow()}>
          {children}
        </span>
      ) : (
        <div className={className}>
          <div className="input-group" onClick={() => !readOnly && handleSelectDateShow()}>
            {(selectedMode === DAY_PICKER_MODE.RANGE) && (
              <DatePeriod aria-label="date" className="form-control" start={dates && dates.from} end={dates && dates.to} />
            )}
            {(selectedMode === DAY_PICKER_MODE.SINGLE) && (
              <DatePeriod aria-label="date" className="form-control" start={dates} end={dates}/>
            )}
            {(selectedMode === DAY_PICKER_MODE.MULTIPLE) && (
              <div aria-label="date" className="form-control" style={{display: 'block'}} start={dates}>
                {dates && dates.sort((a, b) => a && b ? a.getTime() - b.getTime() : 0).map((date, i) => (
                  <span key={i} style={{paddingRight: '10px', whiteSpace: 'nowrap', display: 'inline-block'}}><DateFormat.Day date={date} /></span>
                ))}
              </div>
            )}
              {dates && (
                  <div style={{top: '11px'}} className="input-group-text opacity-50 position-absolute me-4 end-0" onClick={handleErase}>
                      <EraseIcon />
                  </div>
              )}
          </div>

        </div>
      )}

      <Modal show={showSelectDate} onHide={handleSelectDateClose}>
        <Modal.Header>{title || t('boarding:select_date.title')}</Modal.Header>
        <Modal.Body>
          {showSelectDate && (
            <>
              <DayPicker {...dayPickerProps} onChange={setDates} onSelect={handleSelect}/>
              <Button.Panel>
                {(apply === APPLY_MODE.BUTTON) && (
                  <Button variant={BUTTON_TYPE.PRIMARY} position={BUTTON_POSITION.LEFT} onClick={handleApplyDate}>{t('common:button.save')}</Button>
                )}
                <Button variant={BUTTON_TYPE.SECONDARY} position={BUTTON_POSITION.RIGHT} onClick={handleCancel}>{t('common:button.cancel')}</Button>
              </Button.Panel>
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

DayPickerInput.propTypes = {
  ...DayPicker.propTypes,
  apply: PropTypes.string,
  erase: PropTypes.bool,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  onCancel: PropTypes.func,
};

DayPicker.Input = DayPickerInput;

export default DayPicker;